import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "./LandingPage.css";
import Loader from "./Loader";
import { loadContent, DEFAULT_CONTENT } from "../services/contentService";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "../firebase";

import { useNavigate } from "react-router-dom";
import {
  loadGoogleMaps,
  calculateDistance,
  extractCity,
  getCityFromComponents,
  isAllowedCity,
} from "../utils/googleMaps";
import { showToast } from "./Toast";
import OtpVerifyModal from "./OtpVerifyModal";
import { isPhoneVerified, checkUserExists } from "../services/otpService";
import { findLiveRequest } from "../utils/liveRequest";
import { ArrowIcon, withArrows } from "./TxArrow";

/* Campaign banner tokens. Lets ops write the banner copy once with
   placeholders — {from} {to} {reward} {slug} {fromCode} {toCode} — so it
   tracks whichever route campaign is live instead of repeating the amount in
   a second place that can drift out of sync. */
const CAMPAIGN_TOKEN_RE = /\{(from|to|reward|slug|fromCode|toCode)\}/;


/* The header slot is a few hundred pixels between the logo and the nav — a
   full promo sentence cannot fit. With a live campaign we build a compact
   route label from the config; otherwise the banner text is ellipsised. */
const compactPromo = (c, fallback) =>
  c ? `${c.fromCity} → ${c.toCity} · ₹${c.reward} reward` : fallback;

const fillCampaignTokens = (str, c) => {
  if (!str || !c) return str || "";
  return str
    .replace(/\{from\}/g, c.fromCity || "")
    .replace(/\{to\}/g, c.toCity || "")
    .replace(/\{reward\}/g, c.reward ?? "")
    .replace(/\{slug\}/g, c.slug || "")
    .replace(/\{fromCode\}/g, c.fromCode || c.fromCity || "")
    .replace(/\{toCode\}/g, c.toCode || c.toCity || "");
};

/* ─── SVG icon set (Lucide-style, 22 × 22, 1.8px stroke) ─── */
const IC = {
  route: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  match: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  plane: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 2c-2-2-4-2-5.5-.5L10 5 1.8 6.2c-.8.2-1.3.8-1.1 1.7l2.7 4.5c.3.5 1 .6 1.5.2L7.5 10l3.5 8.5c.2.5.8.8 1.3.6l4.5-2c.7-.2.8-.9 1-1.9z"/>
    </svg>
  ),
  rupee: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12M6 8h12m-7.5 5 7.5 8M6 13h3c6.667 0 6.667-10 0-10"/>
    </svg>
  ),
  doc: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14 2z"/>
      <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  search: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
  ),
  handoff: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/>
      <path d="M16.5 9.4 7.55 4.24"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/>
      <circle cx="18.5" cy="15.5" r="2.5"/><path d="M20.27 17.27 22 19"/>
    </svg>
  ),
  check: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  zap: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  shield: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
  tag: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
  /* Replacements for emoji icons (compact 18×18 strokes) */
  travellerSm: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  ),
  senderSm: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  play: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z"/>
    </svg>
  ),
  star: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <polygon points="12 2 15.1 8.6 22 9.5 17 14.4 18.2 21.4 12 18 5.8 21.4 7 14.4 2 9.5 8.9 8.6"/>
    </svg>
  ),
  wave: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/>
      <path d="M3 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/>
    </svg>
  ),
};

const HOW_IT_WORKS = {
  traveller: [
    { n: "01", icon: IC.route,  title: "Share your flight",        desc: "Tell us your route, date and how much you can carry." },
    { n: "02", icon: IC.match,  title: "We match you with a sender", desc: "When a sender has a request on your route, our team reviews it and reaches out on your number." },
    { n: "03", icon: IC.plane,  title: "Carry on your flight",      desc: "The sender brings the document to you before the flight. You carry it on board." },
    { n: "04", icon: IC.rupee,  title: "Paid after handover",       desc: "Once the receiver collects the document at the destination, payment is released to your UPI. Amount shared upfront." },
  ],
  sender: [
    { n: "01", icon: IC.doc,     title: "Post your document",       desc: "Share your pickup city, destination, and what you're sending. Our team will reach out via SMS to take it forward." },
    { n: "02", icon: IC.search,  title: "We find a traveller",      desc: "We check for a verified traveller on your route, review the match, and share their details on your number." },
    { n: "03", icon: IC.handoff, title: "You bring the document",   desc: "Hand it to the traveller before their flight — yourself, or through someone you trust." },
    { n: "04", icon: IC.check,   title: "Receiver collects",        desc: "Your receiver picks up directly from the traveller at the destination. You arrange that meetup — TurantX shares contact details." },
  ],
};

const SENDER_BENEFITS = [
  {
    icon: IC.zap,
    title: "Faster than couriers, when it matters",
    desc: "No warehouses. No sorting hubs. Your document flies in hours — on a real flight, with a verified traveller.",
  },
  {
    icon: IC.handoff,
    title: "Simple to post, easy to track",
    desc: "Share your cities and what you're sending. We find a match, verify both sides, and keep you updated via SMS.",
  },
];

const TRAVELLER_BENEFITS = [
  {
    icon: IC.rupee,
    title: "Earn on a trip you're already taking",
    desc: "Carry a small envelope, get paid to your UPI once the receiver collects at the destination. Amount shown upfront.",
  },
  {
    icon: IC.plane,
    title: "Zero extra work for you",
    desc: "Sender brings the document to you before the flight. Their receiver collects it at the other end. You just fly.",
  },
];


export default function LandingPage() {
  const navigate = useNavigate();
  const savedPhone = localStorage.getItem("PHONE_NUMBER") || "";
  const [showOtp, setShowOtp] = useState(false);
  const heroRef = useRef(null);
  const howRef = useRef(null);
  const statsRef = useRef(null);
  const heroTextRef = useRef(null);
  const heroFormRef = useRef(null);
  const heroBgRef = useRef(null);
  const finalCtaRef = useRef(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // 0 = I'm Travelling, 1 = I'm Sending. Traveller is the default on every
  // screen (decided 2026-10-21; mobile used to open on Sending).
  const [activeRole, setActiveRole] = useState(0);
  const [mobileShowLogin, setMobileShowLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [howTab, setHowTab] = useState("traveller");
  const [faqTab, setFaqTab] = useState("sender");
  const [openFaq, setOpenFaq] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [statCounts, setStatCounts] = useState(DEFAULT_CONTENT.stats.map(() => 0));
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [campaign, setCampaign] = useState(null);

  /* The header is position:fixed, so it cannot be pushed down by normal flow.
     Publish the banner's measured height as a CSS variable instead — the
     header and the hero's negative margin both offset by it. A callback ref
     (not an effect) because the banner renders after an early return, where
     hooks cannot go. --tx-safe-top is zeroed while the banner is up so the
     notch inset is applied once, by the banner, rather than twice. */
  const [scrolledPast, setScrolledPast] = useState(false);
  const [floatDismissed, setFloatDismissed] = useState(false);
  const [bannerH, setBannerH] = useState(0);
  const [promoSlot, setPromoSlot] = useState(null);
  const bannerObserverRef = useRef(null);
  const bannerRef = useCallback((node) => {
    bannerObserverRef.current?.disconnect();
    bannerObserverRef.current = null;
    if (!node) { setBannerH(0); return; }
    const apply = () => setBannerH(node.offsetHeight);
    apply();
    if (typeof ResizeObserver !== "undefined") {
      bannerObserverRef.current = new ResizeObserver(apply);
      bannerObserverRef.current.observe(node);
    }
  }, []);
  const prefersReducedMotion = typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    let cancelled = false;
    loadContent().then((c) => { if (!cancelled) setContent(c); });
    return () => { cancelled = true; };
  }, []);

  /* ============================================================
     Google Maps autocomplete — inline booking
     ============================================================ */
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);
  const [apiError, setApiError] = useState(false);

  const savedAddrs = (() => {
    try {
      const s = sessionStorage.getItem("addressSelectionData");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  })();

  const [fromInput, setFromInput] = useState(savedAddrs?.from?.fullAddress || "");
  const [toInput, setToInput] = useState(savedAddrs?.to?.fullAddress || "");
  const [fromAddress, setFromAddress] = useState(savedAddrs?.from?.fullAddress || "");
  const [toAddress, setToAddress] = useState(savedAddrs?.to?.fullAddress || "");
  const [fromCoords, setFromCoords] = useState(
    savedAddrs?.from ? { lat: savedAddrs.from.latitude, lng: savedAddrs.from.longitude } : null
  );
  const [toCoords, setToCoords] = useState(
    savedAddrs?.to ? { lat: savedAddrs.to.latitude, lng: savedAddrs.to.longitude } : null
  );
  const [fromComponents, setFromComponents] = useState([]);
  const [toComponents, setToComponents] = useState([]);
  const [fromCity, setFromCity] = useState(savedAddrs?.from?.city || null);
  const [toCity, setToCity] = useState(savedAddrs?.to?.city || null);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showFromDrop, setShowFromDrop] = useState(false);
  const [showToDrop, setShowToDrop] = useState(false);
  const [distance, setDistance] = useState(savedAddrs?.distance || null);
  const [routeError, setRouteError] = useState("");

  /* ---------- Load Google Maps ---------- */
  useEffect(() => {
    if (!apiKey) return;
    loadGoogleMaps(apiKey)
      .then((google) => {
        autocompleteService.current = new google.maps.places.AutocompleteService();
        placesService.current = new google.maps.places.PlacesService(document.createElement("div"));
      })
      .catch(() => setApiError(true));
  }, [apiKey]);

  /* ---------- Theme — always light ---------- */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    document.body.classList.add("is-landing");
    return () => document.body.classList.remove("is-landing");
  }, []);

  /* ---------- openTrackMode from sessionStorage (mobile bottom nav) ---------- */
  useEffect(() => {
    if (sessionStorage.getItem("openTrackMode") === "true") {
      sessionStorage.removeItem("openTrackMode");
      setMobileShowLogin(true);
    }
    if (sessionStorage.getItem("openSendMode") === "true") {
      sessionStorage.removeItem("openSendMode");
      setActiveRole(1);
      // No mobile-sheet overlay — form is already inline-visible on mobile;
      // just scroll the user to the hero so they see the right tab pre-selected.
      requestAnimationFrame(() => heroRef.current?.scrollIntoView({ behavior: "smooth" }));
    }
    if (sessionStorage.getItem("openEarnMode") === "true") {
      sessionStorage.removeItem("openEarnMode");
      setActiveRole(0);
      requestAnimationFrame(() => heroRef.current?.scrollIntoView({ behavior: "smooth" }));
    }
  }, []);

  /* ---------- body scroll lock when mobile sheet open ---------- */
  useEffect(() => {
    const cls = "tx-sheet-open";
    if (mobileShowLogin) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [mobileShowLogin]);

  /* ---------- listen for Send / Earn taps from mobile bottom nav ---------- */
  useEffect(() => {
    const pulseForm = () => {
      const el = heroFormRef.current;
      if (!el) return;
      el.classList.remove("hero-form-pulse");
      void el.offsetWidth;
      el.classList.add("hero-form-pulse");
      setTimeout(() => el.classList.remove("hero-form-pulse"), 900);
    };
    const onOpenSend = () => {
      setActiveRole(1);
      heroRef.current?.scrollIntoView({ behavior: "smooth" });
      pulseForm();
    };
    const onOpenEarn = () => {
      setActiveRole(0);
      heroRef.current?.scrollIntoView({ behavior: "smooth" });
      pulseForm();
    };
    window.addEventListener("tx:openSendSheet", onOpenSend);
    window.addEventListener("tx:openEarnSheet", onOpenEarn);
    return () => {
      window.removeEventListener("tx:openSendSheet", onOpenSend);
      window.removeEventListener("tx:openEarnSheet", onOpenEarn);
    };
  }, []);

  /* ---------- scroll-reveal ---------- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("lp-visible");
          observer.unobserve(e.target); // one-shot per section, frees up the observer
        }
      }),
      {
        // Fire ~10% before the section's top edge reaches the viewport bottom,
        // so by the time the user scrolls into it, content is already animating in.
        rootMargin: "0px 0px -10% 0px",
        threshold: 0.05,
      }
    );
    document.querySelectorAll(".lp-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* ---------- Stats counter animation ---------- */
  useEffect(() => {
    if (!statsRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.4 }
    );
    observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);


  /* ---------- Header transparency: tag body while hero is on screen ----------
     Desktop only — the targeted styles (.app-header nav links etc) are hidden
     on mobile, so the body-class toggle here has no visual effect on mobile,
     but it forces a full document style recalc on every intersection change.
     That recalc was a big contributor to iPhone WebKit scroll jank. */
  useEffect(() => {
    if (!heroRef.current) return;
    if (typeof window !== "undefined" && window.innerWidth <= 900) return;
    const cls = "tx-on-hero";
    document.body.classList.add(cls);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) document.body.classList.add(cls);
        else document.body.classList.remove(cls);
      },
      { rootMargin: "-64px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(heroRef.current);
    return () => {
      observer.disconnect();
      document.body.classList.remove(cls);
    };
  }, []);


  useEffect(() => {
    if (!statsVisible) return;
    const duration = 1800;
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setStatCounts(content.stats.map((s) => Math.round(eased * s.num)));
      if (progress >= 1) clearInterval(interval);
    }, 16);
    return () => clearInterval(interval);
  }, [statsVisible, content.stats]);

  /* ---------- autocomplete fetch ---------- */
  const fetchSuggestions = useCallback((input, setSuggestions, setShow) => {
    if (!input || input.length < 2) {
      setSuggestions([]);
      setShow(false);
      return;
    }
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      autocompleteService.current?.getPlacePredictions(
        { input, componentRestrictions: { country: "IN" } },
        (predictions, status) => {
          if (status === "OK" && predictions) {
            const filtered = predictions.filter((p) => isAllowedCity(p.description));
            setSuggestions(filtered);
            setShow(filtered.length > 0);
          } else {
            setSuggestions([]);
            setShow(false);
          }
        }
      );
    }, 250);
  }, []);

  const selectSuggestion = (prediction, type) => {
    placesService.current?.getDetails(
      { placeId: prediction.place_id, fields: ["formatted_address", "geometry", "address_components"] },
      (place, status) => {
        if (status !== "OK" || !place?.geometry) return;

        const address = place.formatted_address;
        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        const components = place.address_components || [];
        const city = extractCity(prediction.description) || getCityFromComponents(components) || extractCity(address);

        if (type === "from") {
          setFromInput(address); setFromAddress(address);
          setFromCoords(coords); setFromComponents(components);
          setFromCity(city);
          setFromSuggestions([]); setShowFromDrop(false);
        } else {
          setToInput(address); setToAddress(address);
          setToCoords(coords); setToComponents(components);
          setToCity(city);
          setToSuggestions([]); setShowToDrop(false);
        }
      }
    );
  };

  /* ---------- persist + distance ---------- */
  useEffect(() => {
    const fromData = fromAddress ? {
      fullAddress: fromAddress, city: fromCity,
      latitude: fromCoords?.lat || null, longitude: fromCoords?.lng || null,
    } : null;
    const toData = toAddress ? {
      fullAddress: toAddress, city: toCity,
      latitude: toCoords?.lat || null, longitude: toCoords?.lng || null,
    } : null;
    if (!fromData && !toData) {
      sessionStorage.removeItem("addressSelectionData");
    } else {
      sessionStorage.setItem("addressSelectionData", JSON.stringify({ from: fromData, to: toData, distance }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromAddress, toAddress]);

  useEffect(() => {
    if (!fromAddress || !toAddress || !fromCoords || !toCoords) return;
    if (fromCity && toCity && fromCity === toCity) {
      setRouteError("Pickup and destination cities must be different.");
      setDistance(null);
      return;
    }
    setRouteError("");
    setDistance(calculateDistance(fromCoords.lat, fromCoords.lng, toCoords.lat, toCoords.lng));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCity, toCity, fromAddress, toAddress]);

  const clearFrom = () => {
    setFromInput(""); setFromAddress(""); setFromCity(null);
    setFromCoords(null); setFromComponents([]);
    setFromSuggestions([]); setShowFromDrop(false);
    setDistance(null); setRouteError("");
  };
  const clearTo = () => {
    setToInput(""); setToAddress(""); setToCity(null);
    setToCoords(null); setToComponents([]);
    setToSuggestions([]); setShowToDrop(false);
    setDistance(null); setRouteError("");
  };

  /* ---------- helpers ---------- */
  const checkUserStatus = async (phone) => {
    try {
      const { exists, role, hasRequest } = await checkUserExists(phone);
      if (!exists) return { exists: false, role: null };
      if (!role || !hasRequest) return { exists: true, role: null };
      return { exists: true, role: role.toUpperCase() };
    } catch { return { exists: false, role: null }; }
  };

  const waitMinLoader = async (startTime, min = 2500) => {
    const elapsed = Date.now() - startTime;
    if (elapsed < min) await new Promise((res) => setTimeout(res, min - elapsed));
  };

  const canContinue = !!(fromAddress && toAddress && distance && !routeError);

  const handleContinue = async () => {
    if (!canContinue) {
      if (!fromAddress) { showToast("Enter your sending from city", "warning"); return; }
      if (!toAddress) { showToast("Enter your sending to city", "warning"); return; }
      if (routeError) { showToast(routeError, "warning"); return; }
      return;
    }
    // Logged-in user with an active request can't start a new one — send to dashboard early
    const existingPhone = localStorage.getItem("PHONE_NUMBER") || "";
    if (existingPhone) {
      const live = await findLiveRequest(existingPhone);
      if (live) {
        showToast("You already have an active request. Complete or cancel it first.", "warning");
        navigate("/dashboard", { state: { phoneNumber: existingPhone } });
        return;
      }
    }
    const userType = activeRole === 0 ? "TRAVELER" : "SENDER";
    localStorage.setItem("USER_ROLE", userType);

    const from = {
      city: fromCity || getCityFromComponents(fromComponents) || extractCity(fromAddress) || fromAddress,
      fullAddress: fromAddress,
      latitude: fromCoords?.lat || null,
      longitude: fromCoords?.lng || null,
    };
    const to = {
      city: toCity || getCityFromComponents(toComponents) || extractCity(toAddress) || toAddress,
      fullAddress: toAddress,
      latitude: toCoords?.lat || null,
      longitude: toCoords?.lng || null,
    };
    const resolvedPhone = localStorage.getItem("PHONE_NUMBER") || "";

    if (userType === "SENDER") {
      navigate("/item-details", { state: { phoneNumber: resolvedPhone, userType, from, to, distance } });
    } else {
      navigate("/flight-details", { state: { phoneNumber: resolvedPhone, userType, from, to, distance } });
    }
  };

  const handleReturningUser = async () => {
    const startTime = Date.now();
    setLoading(true);
    const { exists, role: existingRole } = await checkUserStatus(savedPhone);
    if (exists && existingRole) {
      await waitMinLoader(startTime);
      setLoading(false);
      // Gate: require an OTP-verified session before opening their dashboard
      if (!isPhoneVerified(savedPhone)) {
        setShowOtp(true);
        return;
      }
      navigate("/dashboard", { state: { phoneNumber: savedPhone } });
    } else {
      setLoading(false);
    }
  };

  /* ---------- Hero motion ----------
     Every effect below is desktop-only: the parallaxes are mouse/scroll driven
     and the entrance timeline is explicitly the "cinematic" desktop variant.
     Mobile previously used gsap for ONE 0.45s fade, which meant every phone
     downloaded ~100 KB of gsap + ScrollTrigger inside the initial bundle to
     play something CSS does for free. Mobile now returns before gsap is even
     requested (the fade lives in LandingPage.css as .hero-entrance), and on
     desktop gsap is a dynamic import, so it is a separate chunk fetched after
     first paint instead of blocking it. ------------------------------------ */
  useEffect(() => {
    // Capture refs at mount time so cleanup can safely reference them
    const heroEl   = heroRef.current;
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 900;
    if (isMobile) return; // CSS handles the mobile entrance — no gsap at all

    let ctx = null;
    let cancelled = false;

    (async () => {
      const [{ default: gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return; // unmounted while the chunk was in flight
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {

      // ── 1. Hero entrance timeline ──
      {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

        // Background orbs bloom in first (behind everything)
        tl.from(".hero-orb-1", { scale: 0.4, autoAlpha: 0, duration: 2.0, ease: "power2.out" }, 0)
          .from(".hero-orb-2", { scale: 0.3, autoAlpha: 0, duration: 2.2, ease: "power2.out" }, 0.08)
          .from(".hero-orb-3", { scale: 0.3, autoAlpha: 0, duration: 1.6, ease: "power2.out" }, 0.04);

        // Tagline slides in
        tl.from(".brand-tagline", { y: 20, autoAlpha: 0, duration: 0.55 }, 0.22);

        // Headline words stagger up with a clip effect
        tl.from(".hw", {
            y: 80,
            autoAlpha: 0,
            stagger: 0.09,
            duration: 0.80,
            ease: "power4.out",
          }, 0.34);

        // Value line + badges + demo link cascade
        tl.from(".hero-value-line", { y: 18, autoAlpha: 0, duration: 0.55 }, 0.52)
          .from(".trust-badge",     { y: 14, autoAlpha: 0, stagger: 0.055, duration: 0.40 }, 0.64)
          .from(".hero-demo-link",  { y: 14, autoAlpha: 0, duration: 0.40 }, 0.78);

        // Form card enters from right — slightly delayed, more dramatic
        tl.from(".hero-form-card", {
            x: 48,
            autoAlpha: 0,
            duration: 0.85,
            ease: "power4.out",
          }, 0.26);
      }

      // ── 2. Mouse-tracking micro-parallax on orbs (desktop only) ──
      if (window.innerWidth > 900 && heroEl) {
        let rAF = null;
        const handleMouseMove = (e) => {
          if (rAF) return; // throttle to one rAF per frame
          rAF = requestAnimationFrame(() => {
            rAF = null;
            const { left, top, width, height } = heroEl.getBoundingClientRect();
            const x = (e.clientX - left - width  / 2) / width;
            const y = (e.clientY - top  - height / 2) / height;
            gsap.to(".hero-orb-1", { x: x * -38, y: y * -24, duration: 1.8, ease: "power2.out", overwrite: "auto" });
            gsap.to(".hero-orb-2", { x: x *  28, y: y *  18, duration: 2.0, ease: "power2.out", overwrite: "auto" });
            gsap.to(".hero-orb-3", { x: x * -18, y: y *  12, duration: 1.5, ease: "power2.out", overwrite: "auto" });
            gsap.to(".hero-text-block", { x: x * 6, y: y * 4, duration: 1.4, ease: "power2.out", overwrite: "auto" });
          });
        };
        heroEl.addEventListener("mousemove", handleMouseMove, { passive: true });
        heroEl._gsapMouseMove = handleMouseMove;
      }

      // ── 3. Scroll parallax — quickSetter for zero-jank perf ──
      if (window.innerWidth > 900 && heroTextRef.current && heroFormRef.current && heroBgRef.current) {
        const setTextY = gsap.quickSetter(heroTextRef.current, "y", "px");
        const setFormY = gsap.quickSetter(heroFormRef.current, "y", "px");
        const setBgY   = gsap.quickSetter(heroBgRef.current,   "y", "px");
        ScrollTrigger.create({
          trigger: heroEl,
          start: "top top",
          end: "+=100%",
          onUpdate: (self) => {
            const p = self.progress;
            setTextY(-120 * p);
            setFormY(-55 * p);
            setBgY(-28 * p);
          },
        });
      }

      // ── 4. Magnetic CTA button effect ──
      const loginBtn = document.querySelector(".login-btn.active");
      if (loginBtn) {
        const handleMagnet = (e) => {
          const { left, top, width, height } = loginBtn.getBoundingClientRect();
          const x = (e.clientX - left - width  / 2) * 0.18;
          const y = (e.clientY - top  - height / 2) * 0.18;
          gsap.to(loginBtn, { x, y, duration: 0.35, ease: "power2.out", overwrite: "auto" });
        };
        const handleMagnetLeave = () => {
          gsap.to(loginBtn, { x: 0, y: 0, duration: 0.55, ease: "elastic.out(1, 0.5)", overwrite: "auto" });
        };
        loginBtn.addEventListener("mousemove",  handleMagnet);
        loginBtn.addEventListener("mouseleave", handleMagnetLeave);
        loginBtn._gsapMagnet      = handleMagnet;
        loginBtn._gsapMagnetLeave = handleMagnetLeave;
      }

      }); // end gsap.context
    })();

    // Cleanup mouse listeners not captured by gsap.context
    return () => {
      cancelled = true;
      ctx?.revert(); // may be null if the chunk never finished loading
      if (heroEl?._gsapMouseMove) {
        heroEl.removeEventListener("mousemove", heroEl._gsapMouseMove);
      }
      const btn = document.querySelector(".login-btn");
      if (btn?._gsapMagnet) {
        btn.removeEventListener("mousemove",  btn._gsapMagnet);
        btn.removeEventListener("mouseleave", btn._gsapMagnetLeave);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bannerSlots = [content.banner, content.banner2].filter((b) => b?.active);
  const bannerUsesCampaign = bannerSlots.some((b) =>
    CAMPAIGN_TOKEN_RE.test(`${b.text || ""} ${b.link || ""}`));

  // Only costs a Firestore read when the banner actually references a
  // campaign — an ordinary banner stays free.
  useEffect(() => {
    if (!bannerUsesCampaign) return;
    let cancelled = false;
    getDocs(query(collection(db, "campaignRoutes"), where("active", "==", true), limit(1)))
      .then((snap) => {
        if (cancelled || snap.empty) return;
        const d = snap.docs[0];
        setCampaign({ slug: d.id, ...d.data() });
      })
      .catch(() => { /* banner simply stays hidden */ });
    return () => { cancelled = true; };
  }, [bannerUsesCampaign]);

  const scrollToHow = () => howRef.current?.scrollIntoView({ behavior: "smooth" });
  const scrollToHero = () => heroRef.current?.scrollIntoView({ behavior: "smooth" });

  // A banner whose text still contains unresolved {tokens} means a campaign
  // banner was configured but no matching campaign is live — show nothing
  // rather than a literal "₹{reward}".
  // Pick the banner for the role the visitor has selected in the hero: a slot
  // aimed at exactly this role wins over a "both" slot. A traveller offer is
  // noise to someone sending a document, and vice versa.
  const bannerRole = activeRole === 0 ? "traveller" : "sender";
  const resolvedBanners = bannerSlots
    .map((b) => ({ ...b, text: fillCampaignTokens(b.text, campaign), textShort: fillCampaignTokens(b.textShort, campaign), link: fillCampaignTokens(b.link, campaign) }))
    .filter((b) => !CAMPAIGN_TOKEN_RE.test(`${b.text} ${b.textShort} ${b.link}`) && b.text.trim());
  const activeBanner =
    resolvedBanners.find((b) => b.audience === bannerRole) ||
    resolvedBanners.find((b) => (b.audience || "both") === "both") ||
    null;
  const bannerText = activeBanner?.text || "";
  const bannerTextShort = activeBanner?.textShort?.trim() || "";
  const bannerLink = activeBanner?.link || "";

  const showBanner = !!activeBanner;
  // Float mode: once the page is scrolled, the top bar is replaced by a
  // compact floating card below the header, so the promo keeps a presence
  // without taxing the viewport permanently at rest.
  const showBar = showBanner && !scrolledPast;
  const showHeaderPromo = showBanner && scrolledPast && !floatDismissed && !!promoSlot;

  useEffect(() => { setPromoSlot(document.getElementById("tx-header-promo")); }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrolledPast(window.scrollY > 140);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // The header is position:fixed, so only the resting bar can push it down.
  // Once the banner floats (or goes away) the header returns to the top.
  useEffect(() => {
    const root = document.documentElement;
    if (showBar && bannerH) {
      root.style.setProperty("--tx-banner-h", `${bannerH}px`);
      root.style.setProperty("--tx-safe-top", "0px");
    } else {
      root.style.removeProperty("--tx-banner-h");
      root.style.removeProperty("--tx-safe-top");
    }
    return () => {
      root.style.removeProperty("--tx-banner-h");
      root.style.removeProperty("--tx-safe-top");
    };
  }, [showBar, bannerH]);

  if (loading) return <Loader />;

  return (
    <div className="landing-page">

      {showOtp && (
        <OtpVerifyModal
          phone={savedPhone}
          title="Verify to view your order"
          onClose={() => setShowOtp(false)}
          onVerified={() => { setShowOtp(false); navigate("/dashboard", { state: { phoneNumber: savedPhone } }); }}
        />
      )}

      {showHeaderPromo && createPortal(
        <div className="header-promo">
          <a href={bannerLink || "#"} className="header-promo-link">
            {/* Two lengths, swapped by CSS: the full route label overflows the
                centre slot on a ~390px screen and would ellipsis mid-word. */}
            <span className="header-promo-full">{withArrows(compactPromo(campaign, bannerText))}</span>
            <span className="header-promo-short">
              {campaign ? `₹${campaign.reward} reward` : bannerText}
            </span>
            <span className="header-promo-arrow" aria-hidden><ArrowIcon /></span>
          </a>
          <button
            className="header-promo-close"
            onClick={() => setFloatDismissed(true)}
            aria-label="Dismiss offer"
          >×</button>
        </div>,
        promoSlot
      )}

      {showBar && (
        <div
          ref={bannerRef}
          key={bannerText}
          className={`lp-banner lp-banner-${activeBanner.style || "info"}`}
          role="status"
        >
          {(() => {
            const inner = (
              <>
                {/* Phones get the short copy when ops wrote one (CSS swaps them). */}
                <span className={`lp-banner-msg${bannerTextShort ? " lp-banner-msg--has-short" : ""}`}>{withArrows(bannerText)}</span>
                {bannerTextShort && <span className="lp-banner-msg lp-banner-msg-short">{withArrows(bannerTextShort)}</span>}
                {bannerLink && (
                  <svg className="lp-banner-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                )}
              </>
            );
            return bannerLink
              ? <a href={bannerLink} className="lp-banner-text">{inner}</a>
              : <span className="lp-banner-text">{inner}</span>;
          })()}
        </div>
      )}

      {/* ═══════════════ SECTION 1 — HERO ═══════════════ */}
      <section className="lp-hero" data-theme="dark" ref={heroRef}>

        {/* Background layers: glow, grain, orbs, dot-grid */}
        <div className="hero-bg" ref={heroBgRef}>
          <div className="hero-grain" />
          <div className="hero-orbs" aria-hidden="true">
            <div className="hero-orb hero-orb-1" />
            <div className="hero-orb hero-orb-2" />
            <div className="hero-orb hero-orb-3" />
          </div>
          <div className="hero-grid" aria-hidden="true" />
        </div>

        {mobileShowLogin && (
          <div className="mobile-sheet-overlay" onClick={() => setMobileShowLogin(false)} />
        )}

        {/* ── Centered content ── */}
        <div className="hero-center">

          {/* Heading block */}
          <div className="hero-text-block" ref={heroTextRef}>
            <h1>
              <span className="hw">{content.hero.headlinePart1 || "SEND."}</span>
              {content.hero.headlinePart2 && <><br /><span className="hw">{content.hero.headlinePart2}</span></>}
            </h1>
            <p className="hero-value-line">
              <span className="hero-value-desktop">
                {content.hero.subtextDesktop}
              </span>
              <span className="hero-value-mobile">
                {content.hero.subtextMobile}
              </span>
            </p>
            <div className="tx-proof-chip" aria-label="Pilot status">
              <span className="tx-proof-chip-meta"><strong>Pilot phase</strong> · {content.cities?.length || 5} cities in India</span>
            </div>
            <div className="trust-badges">
              <span className="trust-badge"><span className="trust-badge-check">✓</span>Verified travellers</span>
              {/* Price badge follows the role toggle. "First delivery free" is a
                  SENDER promise — a traveller pays nothing, they earn, so showing
                  it to them reads like a charge they can't place. Desktop even
                  defaults to the traveller role, so this was the first thing a
                  desktop visitor saw. Both sides stay admin-managed via pricing.*/}
              <span className="trust-badge trust-badge-price">
                <span className="trust-badge-check">✓</span>
                {activeRole === 0
                  ? (
                    // Full label normally; the short one on ≤360px phones, where
                    // both pills on one line overflowed the screen by ~13px.
                    <>
                      <span className="trust-badge-full">Earn ₹{content.pricing?.travellerEarnMin ?? 200}–₹{content.pricing?.travellerEarnMax ?? 800} per trip</span>
                      <span className="trust-badge-short">₹{content.pricing?.travellerEarnMin ?? 200}–₹{content.pricing?.travellerEarnMax ?? 800} / trip</span>
                    </>
                  )
                  : content.pricing?.senderFee === 0
                    ? (content.pricing?.pilotLabel || "First delivery free")
                    : `₹${content.pricing?.senderFee} per delivery`}
              </span>
            </div>
            <button type="button" className="hero-demo-link" onClick={() => navigate("/demo")} aria-label="Watch a demo of how TurantX works">
              <span className="tx-icon tx-icon-sm" aria-hidden>{IC.play}</span>
              Watch how it works
              <span className="hero-how-arrow" aria-hidden>↓</span>
            </button>
          </div>

          {/* Booking card + track CTA */}
          <div className={`hero-form-wrap${mobileShowLogin ? " mobile-open" : ""}`} ref={heroFormRef}>

            {savedPhone && (
              <button type="button" className="returning-banner" onClick={handleReturningUser} aria-label="Welcome back, track your order">
                <span className="returning-wave tx-icon tx-icon-md" aria-hidden>{IC.wave}</span>
                <span className="returning-text">Welcome back</span>
                <span className="returning-cta">Track your order <ArrowIcon /></span>
              </button>
            )}

            <div className="hero-form-card">

              <div className="booking-role-toggle">
                <button
                  type="button"
                  className={`booking-role-btn${activeRole === 0 ? " active" : ""}`}
                  onClick={() => setActiveRole(0)}
                  aria-pressed={activeRole === 0}
                >
                  <span className="tx-icon tx-icon-md" aria-hidden>{IC.travellerSm}</span>
                  I'm Travelling
                </button>
                <button
                  type="button"
                  className={`booking-role-btn${activeRole === 1 ? " active" : ""}`}
                  onClick={() => setActiveRole(1)}
                  aria-pressed={activeRole === 1}
                >
                  <span className="tx-icon tx-icon-md" aria-hidden>{IC.senderSm}</span>
                  I'm Sending
                </button>
              </div>

              {apiError && (
                <div className="booking-api-error">
                  ⚠️ Unable to load address search. Please check your connection and refresh.
                </div>
              )}

              <div className="booking-fields">
                <div className="booking-field">
                  <label className="booking-field-label">
                    <span className="booking-field-dot from" />
                    {activeRole === 0 ? "Flying from" : "Sending from"}
                  </label>
                  <div className="booking-input-wrap">
                    <input
                      className={`booking-input${fromInput ? " has-value" : ""}`}
                      placeholder="Start typing a city or area…"
                      value={fromInput}
                      aria-label={activeRole === 0 ? "Flying from city" : "Sending from"}
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={showFromDrop}
                      aria-controls="from-suggestions"
                      autoComplete="off"
                      onChange={(e) => {
                        setFromInput(e.target.value);
                        setFromAddress(""); setFromCity(null);
                        setDistance(null); setRouteError("");
                        fetchSuggestions(e.target.value, setFromSuggestions, setShowFromDrop);
                      }}
                      onFocus={() => fromSuggestions.length > 0 && setShowFromDrop(true)}
                      onBlur={() => setTimeout(() => setShowFromDrop(false), 150)}
                    />
                    {fromInput && (
                      <button
                        className="booking-input-clear"
                        onMouseDown={(e) => { e.preventDefault(); clearFrom(); }}
                        aria-label="Clear from city"
                      >×</button>
                    )}
                    {showFromDrop && fromSuggestions.length > 0 && (
                      <ul id="from-suggestions" className="booking-dropdown" role="listbox">
                        {fromSuggestions.map((p) => (
                          <li key={p.place_id} role="option" aria-selected={false} onMouseDown={() => selectSuggestion(p, "from")}>
                            <span className="drop-main">{p.structured_formatting?.main_text}</span>
                            <span className="drop-sub">{p.structured_formatting?.secondary_text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="booking-field-separator">
                  <div className="booking-sep-line" />
                  <span className="booking-sep-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                    </svg>
                  </span>
                  <div className="booking-sep-line" />
                </div>

                <div className="booking-field">
                  <label className="booking-field-label">
                    <span className="booking-field-dot to" />
                    {activeRole === 0 ? "Flying to" : "Sending to"}
                  </label>
                  <div className="booking-input-wrap">
                    <input
                      className={`booking-input${toInput ? " has-value" : ""}`}
                      placeholder="Start typing a city or area…"
                      value={toInput}
                      aria-label={activeRole === 0 ? "Flying to city" : "Sending to"}
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={showToDrop}
                      aria-controls="to-suggestions"
                      autoComplete="off"
                      onChange={(e) => {
                        setToInput(e.target.value);
                        setToAddress(""); setToCity(null);
                        setDistance(null); setRouteError("");
                        fetchSuggestions(e.target.value, setToSuggestions, setShowToDrop);
                      }}
                      onFocus={() => toSuggestions.length > 0 && setShowToDrop(true)}
                      onBlur={() => setTimeout(() => setShowToDrop(false), 150)}
                    />
                    {toInput && (
                      <button
                        className="booking-input-clear"
                        onMouseDown={(e) => { e.preventDefault(); clearTo(); }}
                        aria-label="Clear to city"
                      >×</button>
                    )}
                    {showToDrop && toSuggestions.length > 0 && (
                      <ul id="to-suggestions" className="booking-dropdown" role="listbox">
                        {toSuggestions.map((p) => (
                          <li key={p.place_id} role="option" aria-selected={false} onMouseDown={() => selectSuggestion(p, "to")}>
                            <span className="drop-main">{p.structured_formatting?.main_text}</span>
                            <span className="drop-sub">{p.structured_formatting?.secondary_text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {routeError && (
                <div className="booking-route-error">⚠️ {routeError}</div>
              )}
              {distance && !routeError && (
                <div className="booking-distance-pill">
                  <span className="distance-icon">🛫</span>
                  <span className="distance-value">{distance} km</span>
                  <span className="distance-label">approx route</span>
                </div>
              )}

              <button
                className={`login-btn${canContinue ? " active" : ""}`}
                onClick={handleContinue}
                disabled={!canContinue}
                aria-disabled={!canContinue}
              >
                <span className="login-btn-desktop">
                  {activeRole === 0 ? "Continue as traveller" : "Continue as sender"} <ArrowIcon />
                </span>
                <span className="login-btn-mobile">
                  {activeRole === 0 ? "Continue as traveller" : "Continue as sender"} <ArrowIcon />
                </span>
              </button>

              {!canContinue && (
                <p className="login-hint" aria-live="polite">
                  {!fromAddress && !toAddress
                    ? activeRole === 0 ? "Enter flying from and to cities to continue" : "Enter sending from and to cities to continue"
                    : !fromAddress
                    ? activeRole === 0 ? "Enter your flying from city to continue" : "Enter your sending from city to continue"
                    : !toAddress
                    ? activeRole === 0 ? "Enter your flying to city to continue" : "Enter your sending to city to continue"
                    : routeError || ""}
                </p>
              )}

              {activeRole === 1 && (
                <p className="login-docs-note">Documents only during pilot — passports, contracts, legal papers, cheques.</p>
              )}
              <p className="login-note">
                By continuing, you agree to TurantX's&nbsp;
                <a href="/info/terms" className="login-link">Terms</a>
                {" & "}
                <a href="/info/privacy" className="login-link">Privacy Policy</a>
              </p>

              <button
                type="button"
                className="mobile-demo-link"
                onClick={() => navigate("/demo")}
                aria-label="Watch a 30-second demo"
              >
                <span className="mobile-demo-play tx-icon tx-icon-sm" aria-hidden>{IC.play}</span>
                <span>New here? Watch 30-sec demo</span>
              </button>
            </div>

          </div>
        </div>

        {/* Scroll hint */}
        <div className="scroll-hint" onClick={scrollToHow}>
          <span className="scroll-hint-text">Scroll</span>
          <div className="scroll-chevrons">
            <span /><span /><span />
          </div>
        </div>

      </section>



{/* ═══════════════ SECTION 2 — HOW IT WORKS ═══════════════ */}
      <section className="lp-section lp-how lp-reveal" ref={howRef}>
        <div className="lp-container">
          <p className="lp-eyebrow">How it works</p>
          <h2 className="lp-heading">Simple from both sides.</h2>
          <p className="lp-subheading">Pick your role and see the steps.</p>

          <div className="how-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={howTab === "traveller"}
              className={`how-tab${howTab === "traveller" ? " active" : ""}`}
              onClick={() => setHowTab("traveller")}
            >
              <span className="tx-icon tx-icon-md" aria-hidden>{IC.travellerSm}</span>
              I'm a Traveller
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={howTab === "sender"}
              className={`how-tab${howTab === "sender" ? " active" : ""}`}
              onClick={() => setHowTab("sender")}
            >
              <span className="tx-icon tx-icon-md" aria-hidden>{IC.senderSm}</span>
              I'm a Sender
            </button>
          </div>

          <div className="how-steps">
            {HOW_IT_WORKS[howTab].map((step, i) => (
              <div key={i} className="how-step-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="how-step-num">{step.n}</div>
                <div className="how-step-icon">{step.icon}</div>
                <h3 className="how-step-title">{step.title}</h3>
                <p className="how-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="how-demo-cta"
            onClick={() => navigate("/demo")}
          >
            <span className="how-demo-cta-icon tx-icon tx-icon-sm" aria-hidden>{IC.play}</span>
            <span className="how-demo-cta-text">See the full walkthrough</span>
            <span className="how-demo-cta-arrow" aria-hidden><ArrowIcon /></span>
          </button>
        </div>
      </section>

      {/* ═══════════════ SECTION 3 — WHY TURANTX (benefit-first, two sides) ═══════════════ */}
      <section className="lp-section lp-why lp-reveal">
        <div className="lp-container">
          <p className="lp-eyebrow">Why TurantX</p>
          <h2 className="lp-heading">Useful for both sides.</h2>
          <p className="lp-subheading">A faster way to send, and a small earn for travellers already going.</p>

          <div className="why-grid">
            <div className="why-col-label why-col-label--sender">For Senders</div>
            {SENDER_BENEFITS.map((b, i) => (
              <div key={`s-${i}`} className="feature-card why-card why-card--sender" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="feature-icon">{b.icon}</div>
                <h3 className="feature-title">{b.title}</h3>
                <p className="feature-desc">{b.desc}</p>
              </div>
            ))}
            <div className="why-col-label why-col-label--traveller">For Travellers</div>
            {TRAVELLER_BENEFITS.map((b, i) => (
              <div key={`t-${i}`} className="feature-card why-card why-card--traveller" style={{ animationDelay: `${(i + 2) * 0.08}s` }}>
                <div className="feature-icon">{b.icon}</div>
                <h3 className="feature-title">{b.title}</h3>
                <p className="feature-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══════════════ SECTION 3b — TESTIMONIALS ═══════════════ */}
      {content.testimonials && content.testimonials.length > 0 && (
      <section className="lp-section lp-testimonials lp-reveal">
        <div className="lp-container">
          <p className="lp-eyebrow">Pilot users</p>
          <h2 className="lp-heading">Early experiences</h2>
          <p className="lp-subheading">A few notes from people who tried TurantX during the pilot.</p>
          <div className="testimonial-grid">
            <div className="testimonial-track">
            {[...content.testimonials, ...content.testimonials].map((t, i) => (
              <div
                key={i}
                className={`testimonial-card${i % content.testimonials.length === 0 ? " testimonial-card--featured" : ""}`}
                style={{ animationDelay: `${i * 0.1}s` }}
                aria-hidden={i >= content.testimonials.length}
              >
                <div className="testimonial-stars">{"★".repeat(t.stars)}</div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-footer">
                  <div className="testimonial-author-row">
                    <div className="testimonial-avatar">{t.name.charAt(0)}</div>
                    <div className="testimonial-author">
                      <span className="testimonial-name">{t.name}</span>
                      <span className="testimonial-route">{t.city}</span>
                    </div>
                  </div>
                  <div className="testimonial-badges">
                    <span className={`testimonial-role role-${t.role.toLowerCase()}`}>{t.role}</span>
                    <span className="testimonial-time">{t.time}</span>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ═══════════════ SECTION 4 — COVERAGE + STATS ═══════════════ */}
      <section className="lp-section lp-coverage lp-reveal" ref={statsRef}>
        <div className="lp-container">
          <p className="lp-eyebrow">Pilot cities</p>
          <h2 className="lp-heading">Routes we are trying to match right now</h2>

          <div className="city-arc-wrap">
            <svg viewBox="0 0 800 175" className="city-arc-svg" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
              <defs>
                <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#F97316" stopOpacity="0.3" />
                  <stop offset="45%" stopColor="#F97316" stopOpacity="1" />
                  <stop offset="100%" stopColor="#F97316" stopOpacity="0.3" />
                </linearGradient>
              </defs>

              {/* Background track */}
              <path d="M 40 130 Q 400 30 760 130"
                fill="none" stroke="rgba(249,115,22,0.10)" strokeWidth="2.5" strokeLinecap="round" />

              {/* Animated dashed arc */}
              <path d="M 40 130 Q 400 30 760 130"
                fill="none" stroke="url(#arcGrad)" strokeWidth="2"
                strokeLinecap="round" strokeDasharray="6 8"
                className="arc-dash" />

              {/* City dots + labels */}
              {[
                { cx: 40,  cy: 130, label: "Mumbai",    ly: 152 },
                { cx: 220, cy: 93,  label: "Delhi",     ly: 113 },
                { cx: 400, cy: 80,  label: "Bangalore", ly: 100 },
                { cx: 580, cy: 93,  label: "Pune",      ly: 113 },
                { cx: 760, cy: 130, label: "Kolkata",   ly: 152 },
              ].map(({ cx, cy, label, ly }) => (
                <g key={label}>
                  <circle cx={cx} cy={cy} r="9" fill="rgba(249,115,22,0.12)" />
                  <circle cx={cx} cy={cy} r="5" fill="#F97316" className="arc-city-dot" />
                  <text x={cx} y={ly} textAnchor="middle" className="arc-city-label">{label}</text>
                </g>
              ))}

              {/* Plane traveling along arc */}
              <g>
                <path d="M-7,0 L3,-3 L7,0 L3,3 Z M-9,-1.5 L-13,0 L-9,1.5 Z" fill="#F97316" opacity="0.85" />
                {!prefersReducedMotion && (
                  <animateMotion dur="5s" repeatCount="indefinite" rotate="auto"
                    path="M 40 130 Q 400 30 760 130" />
                )}
              </g>
            </svg>

            {/* Mobile-only live-routes ticker — replaces the SVG arc on mobile */}
            <div className="routes-ticker" aria-label={`Currently flying between ${content.cities.join(", ")}`}>
              <div className="routes-ticker-track">
                {[0, 1].map((loopIdx) => (
                  <div className="routes-ticker-group" key={loopIdx} aria-hidden={loopIdx === 1}>
                    {content.cities.map((city, i, arr) => (
                      <span className="routes-ticker-item" key={`${loopIdx}-${city}`}>
                        <span className="routes-ticker-city">
                          <span className="routes-ticker-dot" />
                          {city}
                        </span>
                        {i < arr.length - 1 && (
                          <span className="routes-ticker-sep" aria-hidden="true">
                            <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                              <path d="M1 5h13M14 5l-3-3M14 5l-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        )}
                      </span>
                    ))}
                    {/* connector to next loop — plane */}
                    <span className="routes-ticker-item routes-ticker-item--plane" aria-hidden="true">
                      <span className="routes-ticker-plane">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                        </svg>
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <span className="routes-ticker-caption">— Live routes —</span>
            </div>
          </div>

          <div className="stats-row">
            {content.stats.map((s, i) => (
              <div key={i} className="stat-card">
                <span className="stat-value">
                  {s.prefix}{statCounts[i] ?? s.num}{s.suffix}
                </span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SECTION 5 — FAQ (sender / traveller tabs) ═══════════════ */}
      <section className="lp-section lp-faq lp-reveal">
        <div className="lp-container">
          <p className="lp-eyebrow">Got questions?</p>
          <h2 className="lp-heading">Frequently Asked</h2>

          <div className="how-tabs" role="tablist" aria-label="FAQ audience">
            <button
              type="button"
              role="tab"
              aria-selected={faqTab === "sender"}
              className={`how-tab${faqTab === "sender" ? " active" : ""}`}
              onClick={() => { setFaqTab("sender"); setOpenFaq(null); }}
            >
              <span className="tx-icon tx-icon-md" aria-hidden>{IC.senderSm}</span>
              For Senders
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={faqTab === "traveller"}
              className={`how-tab${faqTab === "traveller" ? " active" : ""}`}
              onClick={() => { setFaqTab("traveller"); setOpenFaq(null); }}
            >
              <span className="tx-icon tx-icon-md" aria-hidden>{IC.travellerSm}</span>
              For Travellers
            </button>
          </div>

          <div className="faq-list">
            {(faqTab === "sender" ? content.faqsSender : content.faqsTraveller).map((item, i) => (
              <div key={`${faqTab}-${i}`} className={`faq-item${openFaq === i ? " open" : ""}`}>
                <button
                  className="faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${faqTab}-${i}`}
                >
                  <span>{item.q}</span>
                  <span className={`faq-chevron${openFaq === i ? " open" : ""}`} />
                </button>
                <div
                  id={`faq-answer-${faqTab}-${i}`}
                  className={`faq-a${openFaq === i ? " open" : ""}`}
                  role="region"
                  aria-hidden={openFaq !== i}
                >
                  <div className="faq-a-inner">{item.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══════════════ SECTION 6 — FINAL CTA ═══════════════ */}
      <section className="lp-section lp-cta-section" ref={finalCtaRef}>
        <div className="lp-cta-bg">
          <div className="cta-orb cta-orb-1" />
          <div className="cta-orb cta-orb-2" />
          <div className="lp-cta-noise" />
        </div>
        <div className="lp-container lp-cta-inner">
          <h2 className="lp-heading cta-heading">
            <span className="cta-heading-desktop">
              Find a verified traveller<br />
              <span className="cta-heading-gradient">for your route.</span>
            </span>
            <span className="cta-heading-mobile">
              Find a verified traveller <span className="cta-heading-gradient">for your route.</span>
            </span>
          </h2>

          <div className="cta-trust-chips">
            <span className="cta-trust-chip">✈ Flight + ID verified</span>
            <span className="cta-trust-chip">100% hand-reviewed</span>
            <span className="cta-trust-chip">{content.cities?.length || 5} cities</span>
          </div>

          <div className="cta-buttons">
            <button className="cta-btn cta-primary" onClick={() => { setActiveRole(1); scrollToHero(); }}>
              <span>Send a document</span>
              <span className="cta-primary-arrow"><ArrowIcon /></span>
            </button>
            <button className="cta-btn cta-secondary" onClick={scrollToHow}>
              See how it works
            </button>
          </div>

          {/* Switch the hero to the traveller form as well as scrolling to it —
              on mobile the hero defaults to "I'm Sending", so this link used to
              land a traveller on the sender form. */}
          <button className="cta-traveller-link" onClick={() => { setActiveRole(0); scrollToHero(); }}>
            Flying soon? <span>Earn while you travel <ArrowIcon /></span>
          </button>
        </div>
      </section>


    </div>
  );
}
